/**
 * «Как собрать на декабрь» — разбор нехватки по отрезкам вперёд (04.11.2026).
 *
 * Показывается, только когда не хватает в этом отрезке, в следующем или через
 * один. Отвечает на «откуда взять», а не «почему так»: каждый шаг — деньги,
 * которые у человека уже есть или перестанут уходить, с суммой и местом, где
 * это сделать. Числа считаются здесь, тем же ядром; модель в Pro выбирает
 * порядок и слова, но сумм не придумывает.
 *
 * Порядок шагов — от того, что ничего не стоит, к тому, что откладывает
 * желание: остаток прошлого отрезка, пауза в досрочных платежах, деньги
 * цели-покупки. Урезать траты — только если этого мало, и в рублях в день.
 * Подушку не трогаем: разбор не должен пугать.
 */
import { ZERO, add, clampPos, minM, sub, type IncomeOutlook, type Money } from '@skoka/core';
import type { AppState } from './store.js';

export type GapStepKind = 'carry' | 'loan' | 'goal' | 'spend' | 'savings';

export interface GapStep {
  kind: GapStepKind;
  /** Кредит или цель, которые открывает шаг. */
  refId?: string;
  title: string;
  sub: string;
  /** Сколько шаг даёт к нехватке. */
  gives: Money;
}

export interface GapPlan {
  /** Отрезки по порядку: месяц и что останется (минус — не хватит). */
  windows: { month: string; left: Money }[];
  /** Месяц, где не хватает, — в предложном: «в декабре». */
  month: string;
  /** Тот же месяц в винительном: «на декабрь». */
  monthAcc: string;
  need: Money;
  steps: GapStep[];
  /** Сумма шагов. */
  covered: Money;
  /** Запасной вариант — если шагов мало. */
  backup: GapStep | null;
  /** Совет без действия: «до выписки плати дебетовой». */
  tip: string | null;
}

const NOM = ['январь', 'февраль', 'март', 'апрель', 'май', 'июнь', 'июль', 'август', 'сентябрь', 'октябрь', 'ноябрь', 'декабрь'];
const PREP = ['январе', 'феврале', 'марте', 'апреле', 'мае', 'июне', 'июле', 'августе', 'сентябре', 'октябре', 'ноябре', 'декабре'];
const GEN = ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня', 'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'];
const monthOf = (o: IncomeOutlook): number => Number(o.from.slice(5, 7)) - 1;
const rub = (m: Money): Money => (Math.round(m / 100) * 100) as Money;
/** Шаг меньше тысячи рублей не стоит строки: разбор должен читаться за секунды. */
const WORTH = 100_000 as Money;

export function gapPlan({ state, windows, fmt }: {
  state: AppState;
  /** Этот отрезок и до двух следующих. */
  windows: readonly IncomeOutlook[];
  fmt: (m: Money) => string;
}): GapPlan | null {
  const t = windows.findIndex((w) => w.left !== null && w.left < ZERO);
  if (t < 0) return null;
  const target = windows[t]!;
  const need = clampPos(sub(ZERO, target.left!));
  const m = monthOf(target);
  const steps: GapStep[] = [];
  let left = need;
  const take = (s: GapStep) => {
    if (left <= ZERO || s.gives <= ZERO || (s.gives < WORTH && s.gives < left)) return;
    steps.push(s); left = clampPos(sub(left, s.gives));
  };

  // 1. Остаток отрезков до нехватки — переносится назначением на её платёж.
  const carry = add(...windows.slice(0, t).map((w) => (w.left !== null && w.left > ZERO ? w.left : ZERO)));
  if (carry > ZERO) {
    take({
      kind: 'carry', gives: minM(carry, left),
      title: `Остаток ${GEN[monthOf(windows[0]!)]}`,
      sub: target.paymentLines[0] ? `отложить на платёж ${Number(target.paymentLines[0].date.slice(8))} ${GEN[Number(target.paymentLines[0].date.slice(5, 7)) - 1]}` : `отложить на ${NOM[m]}`,
    });
  }

  // 2. Досрочные платежи каждый месяц — пауза ничего не стоит: график не меняется.
  const months = t + 1;
  for (const l of state.loans ?? []) {
    const extra = (l as { extraMonthly?: Money }).extraMonthly ?? ZERO;
    if (extra <= ZERO) continue;
    take({
      kind: 'loan', refId: l.id, gives: (extra * months) as Money,
      title: `Пауза в досрочных: ${l.name}`,
      sub: `${fmt(rub(extra))} в месяц сверх графика`,
    });
  }

  // 3. Деньги цели-покупки: желание переносится, а не отменяется. Подушку не трогаем.
  for (const g of state.goals.filter((x) => x.status === 'active' && x.kind === 'purchase')) {
    const saved = add(...state.reserves.filter((r) => r.forKind === 'goal' && r.forId === g.id).map((r) => r.amount));
    if (saved <= ZERO) continue;
    take({
      kind: 'goal', refId: g.id, gives: minM(saved, left),
      title: `Деньги цели «${g.name}»`,
      sub: `цель — позже, отложено ${fmt(rub(saved))}`,
    });
  }

  // 4. Если мало — тратить меньше, в рублях в день отрезка с нехваткой.
  if (left > ZERO) {
    const days = Math.max(1, Math.round((Date.parse(target.to) - Date.parse(state.today)) / 86_400_000) + 1);
    take({
      kind: 'spend', gives: left,
      title: `На ${fmt(rub((left / days) as Money))} в день меньше`,
      sub: `свободных трат, до ${Number(target.to.slice(8))} ${GEN[Number(target.to.slice(5, 7)) - 1]}`,
    });
  }
  const covered = add(...steps.map((s) => s.gives));

  // Запас: сбережения не под цель — если шаги не сработают.
  const goalAccounts = new Set(state.goals.map((g) => g.accountId).filter(Boolean));
  const savings = state.accounts
    .filter((a) => a.kind === 'savings' && !a.archived && !goalAccounts.has(a.id) && a.balance > ZERO)
    .sort((a, b) => b.balance - a.balance)[0];
  // Нужен, только когда разбор просит сэкономить: сэкономить может не выйти.
  const backup: GapStep | null = savings && steps.some((x) => x.kind === 'spend') ? {
    kind: 'savings', refId: savings.id, gives: minM(savings.balance as Money, need),
    title: `«${savings.name}»`,
    sub: `если сэкономить не выйдет — дешевле процентов по карте`,
  } : null;

  // Покупки по карте до выписки увеличат платёж с нехваткой.
  const card = target.paymentLines.find((l) => l.statementDate && l.ref?.kind === 'card');
  const tip = card?.statementDate
    ? `До выписки ${Number(card.statementDate.slice(8))} ${GEN[Number(card.statementDate.slice(5, 7)) - 1]} плати дебетовой картой: покупки по «${card.label}» сейчас добавятся к платежу ${Number(card.date.slice(8))} ${GEN[Number(card.date.slice(5, 7)) - 1]}.`
    : null;

  return {
    windows: windows.map((w) => ({ month: NOM[monthOf(w)]!, left: w.left ?? ZERO })),
    month: PREP[m]!, monthAcc: NOM[m]!, need, steps, covered, backup, tip,
  };
}
